"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSeasonalSeer = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const plausibility_1 = require("../../utils/plausibility");
/**
 * Hook to fetch the trailing-90-day seasonal SEER/EER for every HVAC group across
 * one or more hubs, plus a cross-system combined average for the dashboard.
 *
 * @param hubId - A single hub id, or the household scope set (primary hub plus
 * every linked gateway). The query is partitioned by hub, so a gateway's HVAC
 * groups only come back when the gateway's own id is asked for. Results are
 * merged and the combined average spans every system in the household.
 * @returns Per-group seasonal SEER, combined average, loading state, error, and refetch
 */
const useSeasonalSeer = (hubId) => {
    const [perGroup, setPerGroup] = (0, react_1.useState)([]);
    const [combinedAverage, setCombinedAverage] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    // Collapsed to a primitive on every render so the effect depends on the hub
    // SET, not on the array identity — a caller passing a fresh literal would
    // otherwise re-fire the fetch forever.
    const hubIdKey = (Array.isArray(hubId) ? hubId : [hubId])
        .filter(Boolean)
        .filter((id, i, all) => all.indexOf(id) === i)
        .sort()
        .join(',');
    const fetchSeasonalSeer = (0, react_1.useCallback)(async () => {
        const hubIds = hubIdKey ? hubIdKey.split(',') : [];
        if (hubIds.length === 0) {
            return;
        }
        setLoading(true);
        setError(null);
        // allSettled, not all: an unreachable gateway must not wipe the primary
        // hub's seasonal numbers off the dashboard.
        const results = await Promise.allSettled(hubIds.map((id) => amplifyBootstrap_1.API.graphql({ query: queries_1.getSeasonalSeer, variables: { hubId: id } })));
        const seasonalData = [];
        const seen = new Set();
        let failures = 0;
        let lastError;
        results.forEach((result, index) => {
            if (result.status === 'rejected') {
                failures += 1;
                lastError = result.reason;
                console.error(`Error fetching seasonal SEER for hub ${hubIds[index]}:`, result.reason);
                return;
            }
            const rows = result.value?.data?.getSeasonalSeer || [];
            rows.forEach((row) => {
                const key = `${row.hubId}:${row.groupId}`;
                if (seen.has(key))
                    return;
                seen.add(key);
                seasonalData.push(row);
            });
        });
        // Only a total wipeout is an error — partial results are still worth showing.
        if (failures === hubIds.length) {
            setError(lastError instanceof Error ? lastError.message : 'Failed to fetch seasonal SEER');
            setPerGroup([]);
            setCombinedAverage(null);
            setLoading(false);
            return;
        }
        setPerGroup(seasonalData);
        // daysWithData is not enough on its own: a group can have 90 days of data
        // and still roll up to a SEER no air conditioner can produce (2.4 reached
        // the dashboard). One bad system must not drag the household mean.
        const withData = seasonalData.filter((group) => group.daysWithData > 0 && (0, plausibility_1.isPlausible)('seer', group.seasonalSeerValue));
        const average = withData.length > 0
            ? withData.reduce((sum, group) => sum + group.seasonalSeerValue, 0) / withData.length
            : null;
        setCombinedAverage(average);
        setLoading(false);
    }, [hubIdKey]);
    const refetch = async () => {
        await fetchSeasonalSeer();
    };
    (0, react_1.useEffect)(() => {
        fetchSeasonalSeer();
    }, [fetchSeasonalSeer]);
    return {
        perGroup,
        combinedAverage,
        loading,
        error,
        refetch,
    };
};
exports.useSeasonalSeer = useSeasonalSeer;
exports.default = exports.useSeasonalSeer;
